import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Car,
  Clock,
  MapPin,
  ShieldCheck,
  Users,
} from "lucide-react";
import { routePages } from "../../data/routePages";
import { locations } from "../../data/locations";
import "./RouteLandingPage.css";
import { useEffect } from "react";

export function RouteLandingPage() {
  const { routeSlug } = useParams<{ routeSlug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "instant",
  });
}, [routeSlug]);

  const route = routeSlug ? routePages[routeSlug] : undefined;

  if (!route) {
    return null;
  }

  const origin = locations[route.originId];
  const destination = locations[route.destinationId];

  if (!origin || !destination) {
    return null;
  }

  const originName = t(`locations.${origin.id}.name`);
  const destinationName = t(`locations.${destination.id}.name`);

  const destinationShortDescription = t(
    `locations.${destination.id}.shortDescription`
  );

  const destinationLongDescription = t(
    `locations.${destination.id}.longDescription`
  );

  const isAirportRoute = route.routeType === "airport";
  const isLongDistanceRoute = route.routeType === "long-distance";

  const canonicalUrl = `https://francescotransfers.com/routes/${route.slug}`;

  const relatedRoutes = Object.values(routePages)
    .filter((candidateRoute) => {
      if (candidateRoute.slug === route.slug) {
        return false;
      }

      const sameOrigin = candidateRoute.originId === route.originId;

      const nearbyDestination =
        destination.nearbyLocationIds?.includes(
          candidateRoute.destinationId
        ) ?? false;

      return sameOrigin || nearbyDestination;
    })
    .slice(0, 6);

  const handleBooking = () => {
    navigate("/booking", {
      state: {
        quickBookingData: {
          pickup: route.pickupValue,
          destination: route.destinationValue,
          tripType: "oneWay",
          passengers: "1",
        },
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>
          {t("routePage.seo.title", {
            origin: originName,
            destination: destinationName,
            price: route.price4,
          })}
        </title>

        <meta
          name="description"
          content={t("routePage.seo.description", {
            origin: originName,
            destination: destinationName,
            price: route.price4,
          })}
        />

        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <main className="route-page">
        <section
          className="route-hero"
          style={{
            backgroundImage: `
              linear-gradient(
                135deg,
                rgba(5, 18, 24, 0.90),
                rgba(8, 40, 43, 0.78),
                rgba(5, 18, 24, 0.92)
              ),
              radial-gradient(
                circle at 20% 20%,
                rgba(20, 184, 166, 0.26),
                transparent 34%
              ),
              url("/main.jpg")
            `,
          }}
        >
          <div className="route-hero-content">
            <span className="route-eyebrow">
              {isAirportRoute
                ? t("routePage.eyebrowAirport")
                : isLongDistanceRoute
                  ? t("routePage.eyebrowLongDistance")
                  : t("routePage.eyebrowPrivate")}
            </span>

            <h1>
              {t("routePage.heroTitle", {
                origin: originName,
                destination: destinationName,
              })}
            </h1>

            <p>
              {t("routePage.heroDescription", {
                origin: originName,
                destination: destinationName,
              })}
            </p>

            <button
              type="button"
              className="route-primary-button"
              onClick={handleBooking}
            >
              {t("routePage.bookFrom", {
                price: route.price4,
              })}

              <ArrowRight size={18} />
            </button>
          </div>
        </section>

        <section className="route-summary">
          <div className="route-summary-card">
            <MapPin size={24} />
            <span>{t("routePage.distance")}</span>
            <strong>{route.distance}</strong>
          </div>

          <div className="route-summary-card">
            <Clock size={24} />
            <span>{t("routePage.estimatedTime")}</span>
            <strong>{route.duration}</strong>
          </div>

          <div className="route-summary-card">
            <Users size={24} />
            <span>{t("routePage.passengers4")}</span>

            <strong>
              {t("routePage.fromPrice", {
                price: route.price4,
              })}
            </strong>
          </div>

          <div className="route-summary-card">
            <Car size={24} />
            <span>{t("routePage.passengers8")}</span>

            <strong>
              {t("routePage.fromPrice", {
                price: route.price8,
              })}
            </strong>
          </div>
        </section>

        <section className="route-content">
          <div className="route-content-inner">
            <h2>
              {t("routePage.contentTitle", {
                origin: originName,
                destination: destinationName,
              })}
            </h2>

            <p>
              {t("routePage.contentDescription", {
                origin: originName,
                destination: destinationName,
              })}
            </p>

            <div className="route-benefits">
              <div>
                <ShieldCheck size={22} />
                <span>{t("routePage.benefits.pricing")}</span>
              </div>

              <div>
                <ShieldCheck size={22} />
                <span>{t("routePage.benefits.driver")}</span>
              </div>

              <div>
                <ShieldCheck size={22} />
                <span>{t("routePage.benefits.vehicle")}</span>
              </div>
            </div>

            <section className="route-destination-section">
              <h2>
                {t("routePage.aboutDestination", {
                  destination: destinationName,
                })}
              </h2>

              <p>{destinationLongDescription}</p>
            </section>

            <section className="route-destination-section">
              <h2>
                {t("routePage.destinationHighlights", {
                  destination: destinationName,
                })}
              </h2>

              <div className="route-highlight-grid">
                {destination.highlights.map((highlightId) => (
                  <div
                    key={highlightId}
                    className="route-highlight-card"
                  >
                    <ShieldCheck size={20} />

                    <span>
                      {t(
                        `locations.${destination.id}.highlights.${highlightId}`
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            <section className="route-destination-section">
              <h2>
                {t("routePage.popularAreas", {
                  destination: destinationName,
                })}
              </h2>

              <p>{destinationShortDescription}</p>

              <div className="route-area-list">
                {destination.popularAreas.map((areaId) => (
                  <span key={areaId}>
                    {t(
                      `locations.${destination.id}.popularAreas.${areaId}`
                    )}
                  </span>
                ))}
              </div>
            </section>

            {relatedRoutes.length > 0 && (
              <section className="route-destination-section">
                <h2>{t("routePage.popularTransfers")}</h2>

                <div className="route-related-grid">
                  {relatedRoutes.map((relatedRoute) => {
                    const relatedOrigin =
                      locations[relatedRoute.originId];

                    const relatedDestination =
                      locations[relatedRoute.destinationId];

                    if (!relatedOrigin || !relatedDestination) {
                      return null;
                    }

                    const relatedOriginName = t(
                      `locations.${relatedOrigin.id}.name`
                    );

                    const relatedDestinationName = t(
                      `locations.${relatedDestination.id}.name`
                    );

                    return (
                      <Link
                        key={relatedRoute.slug}
                        to={`/routes/${relatedRoute.slug}`}
                        className="route-related-link"
                      >
                        <span>
                          {relatedOriginName} →{" "}
                          {relatedDestinationName}
                        </span>

                        <ArrowRight size={18} />
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            <button
              type="button"
              className="route-secondary-button"
              onClick={handleBooking}
            >
              {t("routePage.bookTransfer")}
            </button>
          </div>
        </section>
      </main>
    </>
  );
}